import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

// Interface for market data
interface MarketData {
  symbol: string;
  price: string;
  change: string;
  changePercent: string;
  positive: boolean;
}

// Animation for updating values
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Container for the market data in the header
const MarketDataContainer = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 8px 0;
  margin-bottom: 15px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    padding: 6px 0;
    margin-bottom: 10px;
  }
`;

// Individual market item
const MarketItem = styled.div<{ positive: boolean; updated: boolean }>`
  display: flex;
  align-items: center;
  padding: 4px 12px;
  margin-right: 15px;
  background: rgba(18, 34, 64, 0.6);
  border-radius: 20px;
  border: 1px solid ${props => props.positive ? 'rgba(100, 255, 218, 0.3)' : 'rgba(255, 107, 107, 0.3)'};
  white-space: nowrap;
  font-size: 0.85rem;
  animation: ${props => props.updated ? pulse : 'none'} 0.5s ease-in-out;
  
  @media (max-width: 768px) {
    padding: 3px 10px;
    margin-right: 10px;
    font-size: 0.75rem;
  }
`;

// Symbol name
const Symbol = styled.span`
  font-weight: 600;
  color: #ffffff;
  margin-right: 8px;
  
  @media (max-width: 768px) {
    margin-right: 5px;
  }
`;

// Price
const Price = styled.span`
  color: #c4cdda;
  margin-right: 8px;
  
  @media (max-width: 768px) {
    margin-right: 5px;
  }
`;

// Change indicator
const Change = styled.span<{ positive: boolean }>`
  color: ${props => props.positive ? '#64ffda' : '#ff6b6b'};
  font-weight: 600;
  
  &:before {
    content: '${props => props.positive ? '▲' : '▼'}';
    margin-right: 3px;
  }
`;

const HeaderMarketData = () => {
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatedItems, setUpdatedItems] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        // Using the Twelve Data API you provided
        const response = await axios.get('https://api.twelvedata.com/quote', {
          params: {
            apikey: '321fe8eaaeb14566888a0af11951726c',
            symbol: 'EUR/USD,GBP/USD,AUD/USD,USD/JPY,USD/CAD,USD/CHF,XAU/USD,XAG/USD,WTI,AAPL,MSFT'
          }
        });
        
        // Process the response data
        const data = Array.isArray(response.data) ? response.data : [response.data];
        const processedData = data.map((item: any) => ({
          symbol: item.symbol,
          price: parseFloat(item.price).toFixed(item.symbol.includes('/') ? 4 : 2),
          change: parseFloat(item.change).toFixed(2),
          changePercent: parseFloat(item.percent_change).toFixed(2),
          positive: parseFloat(item.change) >= 0
        }));
        
        // Check for updates to trigger animations
        if (marketData.length > 0) {
          const updatedFlags: {[key: string]: boolean} = {};
          processedData.forEach((item, index) => {
            if (marketData[index] && marketData[index].price !== item.price) {
              updatedFlags[item.symbol] = true;
              // Reset the flag after animation
              setTimeout(() => {
                setUpdatedItems(prev => ({ ...prev, [item.symbol]: false }));
              }, 500);
            }
          });
          setUpdatedItems(updatedFlags);
        }
        
        setMarketData(processedData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching market data:', error);
        // Fallback to mock data if API fails
        const mockData: MarketData[] = [
          { symbol: 'EUR/USD', price: '1.1842', change: '+0.0014', changePercent: '+0.12', positive: true },
          { symbol: 'GBP/USD', price: '1.3728', change: '-0.0021', changePercent: '-0.15', positive: false },
          { symbol: 'XAU/USD', price: '1812.45', change: '+8.12', changePercent: '+0.45', positive: true },
          { symbol: 'AAPL', price: '142.36', change: '+1.24', changePercent: '+0.88', positive: true },
          { symbol: 'MSFT', price: '286.74', change: '-0.87', changePercent: '-0.30', positive: false }
        ];
        setMarketData(mockData);
        setLoading(false);
      }
    };

    fetchMarketData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(fetchMarketData, 30000);
    
    return () => clearInterval(interval);
  }, [marketData]);

  if (loading) {
    return (
      <MarketDataContainer>
        <MarketItem positive={true} updated={false}>
          <Symbol>Loading...</Symbol>
        </MarketItem>
      </MarketDataContainer>
    );
  }

  return (
    <MarketDataContainer>
      {marketData.map((item, index) => (
        <MarketItem 
          key={index} 
          positive={item.positive} 
          updated={!!updatedItems[item.symbol]}
        >
          <Symbol>{item.symbol}</Symbol>
          <Price>{item.price}</Price>
          <Change positive={item.positive}>
            {item.change} ({item.changePercent}%)
          </Change>
        </MarketItem>
      ))}
    </MarketDataContainer>
  );
};

export default HeaderMarketData;